/**
 * System Task Schedules API
 * Scheduled system task management endpoints
 * NOTE: Matches backend at /admin/system-task-schedules/*
 */

import apiClient from './client';
import type {
  SystemTaskSchedule,
  SystemTaskScheduleCreateRequest,
  SystemTaskScheduleUpdateRequest,
  SystemTaskScheduleListResponse,
  SystemTaskScheduleToggleResponse,
  SystemTaskScheduleRunResponse,
  SystemTaskScheduleListParams,
  SystemTaskAvailableTypesResponse,
  SystemTaskExecution,
  SystemTaskExecutionListResponse,
  SystemTaskExecutionListParams,
} from '@/types/systemTaskSchedule';

export const systemTaskSchedulesApi = {
  /**
   * List system task schedules
   */
  list: async (params?: SystemTaskScheduleListParams): Promise<SystemTaskScheduleListResponse> => {
    const response = await apiClient.get('/system-task-schedules', { params });
    return response.data;
  },

  /**
   * Get a single schedule by ID
   */
  get: async (scheduleId: string): Promise<SystemTaskSchedule> => {
    const response = await apiClient.get(`/system-task-schedules/${scheduleId}`);
    return response.data;
  },

  /**
   * Create a new system task schedule
   */
  create: async (data: SystemTaskScheduleCreateRequest): Promise<SystemTaskSchedule> => {
    const response = await apiClient.post('/system-task-schedules', data);
    return response.data;
  },

  /**
   * Update an existing schedule
   */
  update: async (scheduleId: string, data: SystemTaskScheduleUpdateRequest): Promise<SystemTaskSchedule> => {
    const response = await apiClient.put(`/system-task-schedules/${scheduleId}`, data);
    return response.data;
  },

  /**
   * Delete a schedule
   */
  delete: async (scheduleId: string): Promise<void> => {
    await apiClient.delete(`/system-task-schedules/${scheduleId}`);
  },

  /**
   * Toggle schedule enabled state
   * @param scheduleId - Schedule ID
   * @param enabled - Optional explicit state (undefined = toggle)
   */
  toggle: async (scheduleId: string, enabled?: boolean): Promise<SystemTaskScheduleToggleResponse> => {
    const params = enabled !== undefined ? { enabled } : {};
    const response = await apiClient.post(`/system-task-schedules/${scheduleId}/toggle`, null, { params });
    return response.data;
  },

  /**
   * Manually trigger a schedule execution immediately
   */
  runNow: async (scheduleId: string): Promise<SystemTaskScheduleRunResponse> => {
    const response = await apiClient.post(`/system-task-schedules/${scheduleId}/run`);
    return response.data;
  },

  /**
   * Get available system task types
   */
  getAvailableTypes: async (): Promise<SystemTaskAvailableTypesResponse> => {
    const response = await apiClient.get('/system-task-schedules/types/available');
    return response.data;
  },

  // ============================================================================
  // Execution History
  // ============================================================================

  /**
   * Get execution history for a specific schedule
   */
  getScheduleExecutions: async (
    scheduleId: string,
    params?: Omit<SystemTaskExecutionListParams, 'schedule_id'>
  ): Promise<SystemTaskExecutionListResponse> => {
    const response = await apiClient.get(`/system-task-schedules/${scheduleId}/executions`, { params });
    return response.data;
  },

  /**
   * List all executions with optional filters
   */
  listAllExecutions: async (params?: SystemTaskExecutionListParams): Promise<SystemTaskExecutionListResponse> => {
    const response = await apiClient.get('/system-task-schedules/executions/all', { params });
    return response.data;
  },

  /**
   * Get a single execution by ID
   */
  getExecution: async (executionId: string): Promise<SystemTaskExecution> => {
    const response = await apiClient.get(`/system-task-schedules/executions/${executionId}`);
    return response.data;
  },
};
